import { useCallback, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../../../components/ui";
import { ApiError } from "../../../lib/http";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import {
  cancelClientAppointment,
  completeClientAppointment,
  createAppointment,
  fetchClientAppointments,
  rescheduleClientAppointment,
} from "../services/appointmentsService";
import { byNewest } from "../appointments";
import {
  clientAppointmentsQueryKey,
  invalidateAppointmentReads,
} from "../appointmentCaches";
import type {
  ClientAppointment,
  NewClientAppointment,
  RescheduleAppointment,
} from "../types/appointments.types";

export type ClientAppointmentsStatus = "loading" | "ready" | "failed";

export interface ClientAppointmentsState {
  /** This client's appointments, newest first. */
  appointments: ClientAppointment[];
  status: ClientAppointmentsStatus;
  /** The server's reason for a failed load, where it gave one. */
  error?: string;
  /** What the failed state's retry calls. */
  reload: () => void;
  /** Books one, and hands back the row as it was stored. */
  schedule: (input: NewClientAppointment) => Promise<ClientAppointment>;
  /** Moves one to a new day, time and length. */
  reschedule: (
    appointmentId: string,
    input: RescheduleAppointment
  ) => Promise<ClientAppointment>;
  /**
   * Calls one off, leaving it in the list as cancelled.
   *
   * Both ways of doing that: an appointment still ahead that is not going to
   * happen, and one whose time has passed without the session taking place.
   * The backend keeps one state for the two — see `cancelClientAppointment`.
   */
  cancel: (appointmentId: string, reason: string) => Promise<ClientAppointment>;
  /** Says the session took place, with the practitioner’s note on it. */
  complete: (
    appointmentId: string,
    note?: string
  ) => Promise<ClientAppointment>;
}

/** Handed out before the first load lands, and the same array every time: a
 *  fresh one per render would rebuild every memo reading it. */
const EMPTY: ClientAppointment[] = [];

// Re-exported rather than declared here: the key is shared with everything
// that has to invalidate this list from outside, so it lives beside the other
// three in `appointmentCaches`. Two copies of a cache key is two chances to
// mistype one and never find out.
export { clientAppointmentsQueryKey };

/**
 * One client's appointments, loaded when their Appointment tab opens.
 *
 * Built the same way `useClientPayments` is, and for the same reasons: cached
 * per client through TanStack Query, so a tab already visited draws
 * immediately while a fresh read runs behind it; and every write is a call
 * whose response — the whole row as the server now holds it — is written
 * through `setQueryData` rather than refetched, so the cache for this client
 * carries the write and a remount does not flash the pre-write row.
 *
 * The list is kept newest first on every write, because the table, the
 * carousel and the strip above the tabs all read it in order and a
 * rescheduled appointment has just moved in time. The three screen pieces
 * filter it for themselves — see `appointments.ts` — but none of them sorts.
 *
 * Must be used inside a Clerk provider and an active session.
 */
export function useClientAppointments(
  clientId: string
): ClientAppointmentsState {
  const getToken = useTokenGetter();
  const toast = useToast();
  const queryClient = useQueryClient();
  const queryKey = clientAppointmentsQueryKey(clientId);

  const query = useQuery({
    queryKey,
    queryFn: ({ signal }) => fetchClientAppointments(getToken, clientId, signal),
  });

  const error =
    query.isError && query.error instanceof ApiError
      ? query.error.message
      : query.isError
      ? "We could not reach the server. Check your connection and try again."
      : undefined;

  // Only fires when there is nothing cached to show in its place — see
  // `useClientPayments` for why that is what `isError` means here.
  useEffect(() => {
    if (!query.isError) return;
    console.error("client appointments failed to load:", query.error);
    toast.error("Could not load this client’s appointments", error ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isError, query.error]);

  /**
   * Puts a row into the cached list — replacing the one it shares an id with,
   * or joining the list where there is none to replace — and re-sorts.
   *
   * One writer for all three calls, because all three answer with a whole row
   * and the only difference between booking and moving is whether an id is
   * already in the list. The re-sort is what a reschedule needs: the row it
   * answers with may belong at the other end of the history.
   *
   * The write lands here and every other cache holding the row is then marked
   * stale — see `invalidateAppointmentReads`. Without that second half, a
   * session marked completed on this tab left My task still offering to join
   * it until somebody reloaded the page.
   */
  const put = useCallback(
    (row: ClientAppointment) => {
      queryClient.setQueryData<ClientAppointment[]>(
        queryKey,
        (current: ClientAppointment[] | undefined) => {
          const list = current ?? EMPTY;
          const known = list.some(
            (appointment: ClientAppointment) => appointment.id === row.id
          );
          const next = known
            ? list.map((appointment: ClientAppointment) =>
                appointment.id === row.id ? row : appointment
              )
            : [...list, row];
          return next.sort(byNewest);
        }
      );

      invalidateAppointmentReads(queryClient, row);
    },
    [queryClient, queryKey]
  );

  const schedule = useCallback(
    async (input: NewClientAppointment) => {
      // Always a client here: this hook is a client's own history, and the
      // dialog it is opened from never asks who.
      const booked = await createAppointment(
        getToken,
        { subject: "client", id: clientId },
        input
      );
      put(booked);
      return booked;
    },
    [getToken, clientId, put]
  );

  // The three writes below reach an appointment by its own id rather than
  // through the client: a booking is a record in its own right once it exists,
  // and the endpoints hang off it. Only the list and the booking need to know
  // whose it is.
  const reschedule = useCallback(
    async (appointmentId: string, input: RescheduleAppointment) => {
      const moved = await rescheduleClientAppointment(
        getToken,
        appointmentId,
        input
      );
      put(moved);
      return moved;
    },
    [getToken, put]
  );

  const cancel = useCallback(
    async (appointmentId: string, reason: string) => {
      const called = await cancelClientAppointment(
        getToken,
        appointmentId,
        reason
      );
      put(called);
      return called;
    },
    [getToken, put]
  );

  const complete = useCallback(
    async (appointmentId: string, note = "") => {
      const done = await completeClientAppointment(
        getToken,
        appointmentId,
        note
      );
      put(done);
      return done;
    },
    [getToken, put]
  );

  return {
    appointments: query.data ?? EMPTY,
    status:
      query.status === "pending"
        ? "loading"
        : query.status === "error"
        ? "failed"
        : "ready",
    error,
    reload: () => {
      void query.refetch();
    },
    schedule,
    reschedule,
    cancel,
    complete,
  };
}
